import React, { useEffect, useRef, useState } from "react";
import { MdDeleteOutline } from "react-icons/md";
import {
  createKnowledgeEvent,
  deleteKnowledgeEvent,
  getAllKnowledgeEvents,
} from "../api/eventApi";
import { getConfig } from "../api/config";
import { isApiSuccess } from "../utils/adminData";
import {
  formatKnowledgeEventDate,
  normalizeKnowledgeEvents,
} from "../utils/knowledgeCenter";

const EMPTY_FORM = {
  title: "",
  description: "",
  eventDate: "",
  image: null,
};

const AdminDashboardPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [formData, setFormData] = useState(EMPTY_FORM);
  const imageInputRef = useRef(null);

  const loadEvents = async (nextMessage = "") => {
    setLoading(true);

    const config = await getConfig();
    const response = await getAllKnowledgeEvents();
    const normalizedEvents = normalizeKnowledgeEvents(response, config.baseUrl);

    setEvents(normalizedEvents);
    setLoading(false);

    if (!isApiSuccess(response) && normalizedEvents.length === 0) {
      setFeedback({
        type: "error",
        message: response?.Message || "Unable to load events right now.",
      });
      return;
    }

    if (nextMessage) {
      setFeedback({
        type: "success",
        message: nextMessage,
      });
    }
  };

  useEffect(() => {
    loadEvents();
  }, []);

  const handleFieldChange = (event) => {
    const { name, value, files, type } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "file" ? files?.[0] || null : value,
    }));
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    if (imageInputRef.current) {
      imageInputRef.current.value = "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setIsSubmitting(true);
    setFeedback({ type: "", message: "" });

    const response = await createKnowledgeEvent({
      title: formData.title.trim(),
      description: formData.description.trim(),
      eventDate: formData.eventDate,
      image: formData.image,
    });

    if (!isApiSuccess(response)) {
      setFeedback({
        type: "error",
        message: response?.Message || "Unable to create event.",
      });
      setIsSubmitting(false);
      return;
    }

    form.reset();
    resetForm();
    setIsSubmitting(false);
    await loadEvents("Event created successfully.");
  };

  const handleDelete = async (eventId) => {
    const confirmed = window.confirm("Are you sure you want to delete this event?");
    if (!confirmed) return;

    setDeletingId(eventId);
    setFeedback({ type: "", message: "" });

    const response = await deleteKnowledgeEvent(eventId);

    if (!isApiSuccess(response)) {
      setFeedback({
        type: "error",
        message: response?.Message || "Unable to delete event.",
      });
      setDeletingId("");
      return;
    }

    setDeletingId("");
    await new Promise((resolve) => setTimeout(resolve, 400));
    await loadEvents("Event deleted successfully.");
  };

  return (
    <div className="space-y-4">
      <section className="rounded-md border border-slate-200 bg-white px-4 py-4 sm:px-5">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Knowledge Center</h1>
        <p className="mt-1 text-sm text-slate-500">Manage knowledge center events.</p>
        <h2 className="mt-4 text-base font-semibold text-slate-900">Add Event</h2>
        {feedback.message ? (
          <div
            className={`mt-4 rounded-md px-4 py-3 text-sm ${
              feedback.type === "error"
                ? "border border-rose-200 bg-rose-50 text-rose-700"
                : "border border-emerald-200 bg-emerald-50 text-emerald-700"
            }`}
          >
            {feedback.message}
          </div>
        ) : null}
        <form className="mt-4 grid gap-4 md:grid-cols-2" onSubmit={handleSubmit}>
          <div className="space-y-1 md:col-span-1">
            <label className="text-sm font-medium text-slate-700" htmlFor="kc-title">
              Title
            </label>
            <inputBooking Date
              id="kc-title"
              name="title"
              type="text"
              value={formData.title}
              onChange={handleFieldChange}
              required
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-400"
            />
          </div>

          <div className="space-y-1 md:col-span-1">
            <label className="text-sm font-medium text-slate-700" htmlFor="kc-event-date">
              Event Date
            </label>
            <input
              id="kc-event-date"
              name="eventDate"
              type="date"
              value={formData.eventDate}
              onChange={handleFieldChange}
              required
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-400"
            />
          </div>

          <div className="space-y-1 md:col-span-2">
            <label className="text-sm font-medium text-slate-700" htmlFor="kc-image">
              Image Upload
            </label>
            <input
              ref={imageInputRef}
              id="kc-image"
              name="image"
              type="file"
              accept="image/*"
              onChange={handleFieldChange}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition file:mr-4 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white focus:border-slate-400"
            />
          </div>

          <div className="space-y-1 md:col-span-2">
            <label className="text-sm font-medium text-slate-700" htmlFor="kc-description">
              Description
            </label>
            <textarea
              id="kc-description"
              name="description"
              value={formData.description}
              onChange={handleFieldChange}
              required
              rows={4}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-slate-400"
            />
          </div>

          {/* <div className="space-y-1 md:col-span-2">
            <label className="text-sm font-medium text-slate-700" htmlFor="kc-image">
              Image Upload
            </label>
            <input
              id="kc-image"
              name="image"
              type="file"
              accept="image/*"
              onChange={handleFieldChange}
              required
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 outline-none transition file:mr-4 file:rounded-md file:border-0 file:bg-slate-900 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white focus:border-slate-400"
            />
          </div> */}

          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md border border-slate-900 bg-slate-900 px-4 py-2 text-sm font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-70"
            >
              {isSubmitting ? "Saving..." : "Create Event"}
            </button>
          </div>
        </form>
      </section>

      <section className="rounded-md border border-slate-200 bg-white px-4 py-4 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900">Existing Events</h2>
            <p className="text-sm text-slate-500">{events.length} event records available.</p>
          </div>
        </div>

        {loading ? (
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-500">
            Loading events...
          </div>
        ) : events.length ? (
          <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {events.map((event) => (
              <article
                key={event.id}
                className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
              >
                <div className="h-48 w-full overflow-hidden bg-slate-100">
                  <img
                    src={event.image}
                    alt={event.title || "Knowledge Center event"}
                    className="h-full w-full object-contain object-center p-2"
                    loading="lazy"
                  />
                </div>
                <div className="space-y-3 p-5">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{event.title || "-"}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">
                      {event.description || "-"}
                    </p>
                  </div>
                  <p className="text-sm text-slate-500">
                    Event Date: {formatKnowledgeEventDate(event.eventDate)}
                  </p>
                  <button
                    type="button"
                    onClick={() => handleDelete(event.id)}
                    disabled={deletingId === event.id}
                    className="inline-flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    <MdDeleteOutline className="text-sm" />
                    {deletingId === event.id ? "Deleting..." : "Delete"}
                  </button>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-500">
            No events available yet.
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminDashboardPage;
